import React, { Component } from 'react';
import moment from 'moment';
import { compose, graphql } from 'react-apollo';
import Tab from 'react-bootstrap/Tab';
import { Helmet } from 'react-helmet';
import { SELECTION_EVENT, trackUserEvent } from 'userActivityMiddleware';
import { SUBSCRIPTION_PREFERRED_LOCATION_CHANGED } from 'containers/DesktopNotificationTray';
import PageTitleBar from 'components/PageTitleBar';
import TabView from 'components/TabView';
import { LG_BREAKPOINT_AND_UP } from 'utils/constants';
import shallowCloneUpdateIn from 'utils/shallowCloneUpdateIn';
import withMediaQuery from 'utils/withMediaQuery';
import QuestList from './QuestList';
import {
  QUERY_GET_QUEST_STATUS,
  SUBSCRIPTION_ONLINE_MISSION_USER_EVENTS,
  SUBSCRIPTION_QUEST_PROGRESS
} from './graphql';
import QuestsIcon from './assets/Button-Quests.svg';
import './QuestsPage.css';

const ACTIVE_QUESTS = 'active-quests';
const PAST_QUESTS = 'past-quests';

const TABS = [
  { key: ACTIVE_QUESTS, title: 'Active Quests' },
  { key: PAST_QUESTS, title: 'Past Quests' }
];

class QuestsPage extends Component {
  componentDidMount() {
    this.unsubscribeFromQuestUpdates = this.props.subscribeToQuestUpdates();
    this.unsubscribeFromOnlineMissionUserEvents =
      this.props.subscribeToOnlineMissionUserEvents();
    this.unsubscribeFromLocationData = this.props.subscribeToLocationData();
  }

  componentWillUnmount() {
    this.unsubscribeFromQuestUpdates && this.unsubscribeFromQuestUpdates();
    this.unsubscribeFromOnlineMissionUserEvents &&
      this.unsubscribeFromOnlineMissionUserEvents();
    this.unsubscribeFromLocationData = () => {};
  }

  handleSelectTab = selectedTab => {
    trackUserEvent({
      activityType: SELECTION_EVENT,
      eventName: 'select-quests-tab',
      eventValue: selectedTab,
      initiator: 'quest-tabs'
    });
  };

  render() {
    const { questStatus, loading, error } = this.props.questStatusData;
    let currentQuests = [];
    let pastQuests = [];
    let localeInfo = '';

    if (questStatus) {
      localeInfo = questStatus.localeInfo;

      const now = moment();
      currentQuests = questStatus.questStatus.filter(
        questStatus =>
          !questStatus.isComplete &&
          !questStatus.utcAbortionDate &&
          !moment
            .utc(questStatus.UTCValidTo, 'YYYY/MM/DD HH:mm:ss')
            .isBefore(now)
      );
      pastQuests = questStatus.questStatus
        .filter(
          questStatus =>
            questStatus.isComplete ||
            questStatus.utcAbortionDate ||
            moment
              .utc(questStatus.UTCValidTo, 'YYYY/MM/DD HH:mm:ss')
              .isBefore(now)
        )
        .sort((questStatus1, questStatus2) => {
          const questStatusMoment1 = moment.utc(
            questStatus1.UTCValidTo,
            'YYYY/MM/DD HH:mm:ss'
          );
          const questStatusMoment2 = moment.utc(
            questStatus2.UTCValidTo,
            'YYYY/MM/DD HH:mm:ss'
          );
          if (questStatusMoment1.isSame(questStatusMoment2)) {
            return 0;
          } else if (questStatusMoment1.isBefore(questStatusMoment2)) {
            return 1;
          } else {
            return -1;
          }
        });
    }

    return (
      <div className="quests-page">
        <Helmet>
          <title>Quests</title>
        </Helmet>
        <h1 className="sr-only">Quests</h1>
        <PageTitleBar title="Quests" icon={QuestsIcon} />
        <div className="quests-container">
          <TabView
            defaultTab={ACTIVE_QUESTS}
            tabTitles={TABS}
            id="quests-navigation"
            className="quests-list-tab-view"
            theme="quests"
            navClassName="quests-tabs"
            dropdownClassName="quests-list-menu"
            matchesBreakpoint={this.props.matchesBreakpoint}
            onSelectTab={this.handleSelectTab}
          >
            <Tab.Content>
              <Tab.Pane eventKey={ACTIVE_QUESTS} mountOnEnter>
                <QuestList
                  localeInfo={localeInfo}
                  questStatuses={currentQuests}
                  loading={loading}
                  error={error}
                  active
                />
              </Tab.Pane>
              <Tab.Pane eventKey={PAST_QUESTS} mountOnEnter>
                <QuestList
                  localeInfo={localeInfo}
                  questStatuses={pastQuests}
                  loading={loading}
                  error={error}
                />
              </Tab.Pane>
            </Tab.Content>
          </TabView>
        </div>
      </div>
    );
  }
}

export default compose(
  withMediaQuery(LG_BREAKPOINT_AND_UP),
  graphql(QUERY_GET_QUEST_STATUS, {
    name: 'questStatusData',
    options: props => ({
      fetchPolicy: 'network-only'
    }),
    props: ({ ownProps, questStatusData }) => ({
      questStatusData,
      subscribeToQuestUpdates: () => {
        return questStatusData.subscribeToMore({
          document: SUBSCRIPTION_QUEST_PROGRESS,
          updateQuery: (
            prev,
            { subscriptionData: { data: questProgressUpdate } }
          ) => {
            if (!questProgressUpdate) {
              return null;
            }
            const result = Object.assign({}, prev, {
              questStatus: questProgressUpdate.progressUpdate
            });
            return result;
          }
        });
      },
      subscribeToOnlineMissionUserEvents: () => {
        return questStatusData.subscribeToMore({
          document: SUBSCRIPTION_ONLINE_MISSION_USER_EVENTS,
          updateQuery: (prev, { subscriptionData: { data } }) => {
            if (!data) {
              return null;
            }
            const { missionId, questId, progress, progressText } = data;
            let result = null;
            try {
              result = shallowCloneUpdateIn(
                prev,
                [
                  'questStatus',
                  'questStatus',
                  questStatuses =>
                    questStatuses.findIndex(qs => qs.quest.id === questId),
                  'missionStatuses',
                  missionStatuses =>
                    missionStatuses.findIndex(as => as.mission.id === missionId)
                ],
                status => Object.assign({}, status, { progress, progressText })
              );
            } catch (e) {
              console.error(e);
            }
            return result;
          }
        });
      },
      subscribeToLocationData: () => {
        return questStatusData.subscribeToMore({
          document: SUBSCRIPTION_PREFERRED_LOCATION_CHANGED,
          updateQuery: () => questStatusData.refetch()
        });
      }
    })
  })
)(QuestsPage);
